import Big from 'big.js';

/** Valor monetário como string decimal canônica (`'1234.56'`) ou `Big`. Nunca `number` em reais. */
export type MoneyInput = string | Big;

/** Centavos inteiros, o formato que o legado grava no fio (`CurrencyFormField`). */
export type Cents = number;

export type MoneyRoundingMode = 'half-even' | 'half-up' | 'down' | 'up';

export const DEFAULT_MONEY_SCALE = 2;
export const DEFAULT_MONEY_ROUNDING: MoneyRoundingMode = 'half-even';

const ROUNDING_MODES: Readonly<Record<MoneyRoundingMode, Big.RoundingMode>> = {
  down: 0,
  'half-up': 1,
  'half-even': 2,
  up: 3,
};

const DECIMAL_STRING_PATTERN = /^[+-]?[0-9]+(\.[0-9]+)?$/;

export class InvalidMoneyValueError extends Error {
  readonly value: string;

  constructor(value: string) {
    super(`"${value}" não é string decimal canônica; use parseMoneyInput() para texto digitado.`);
    this.name = 'InvalidMoneyValueError';
    this.value = value;
  }
}

export function isDecimalString(value: string): boolean {
  return DECIMAL_STRING_PATTERN.test(value.trim());
}

export function toDecimal(value: MoneyInput): Big {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!DECIMAL_STRING_PATTERN.test(trimmed)) throw new InvalidMoneyValueError(value);
  return new Big(trimmed);
}

export interface MoneyScaleOptions {
  readonly scale?: number;
  readonly rounding?: MoneyRoundingMode;
}

/** Arredonda para `scale` casas (padrão 2, meio-para-par) e devolve string decimal. */
export function roundMoney(value: MoneyInput, options: MoneyScaleOptions = {}): string {
  const rounding = ROUNDING_MODES[options.rounding ?? DEFAULT_MONEY_ROUNDING];
  return toDecimal(value).toFixed(options.scale ?? DEFAULT_MONEY_SCALE, rounding);
}

export function addMoney(left: MoneyInput, right: MoneyInput): string {
  return toDecimal(left).plus(toDecimal(right)).toFixed();
}

export function subtractMoney(left: MoneyInput, right: MoneyInput): string {
  return toDecimal(left).minus(toDecimal(right)).toFixed();
}

export function multiplyMoney(value: MoneyInput, factor: MoneyInput): string {
  return toDecimal(value).times(toDecimal(factor)).toFixed();
}

export function compareMoney(left: MoneyInput, right: MoneyInput): -1 | 0 | 1 {
  return toDecimal(left).cmp(toDecimal(right));
}

/** Centavos inteiros → string decimal: `123456` → `'1234.56'`. */
export function centsToDecimal(cents: Cents | string): string {
  const value = typeof cents === 'number' ? String(cents) : cents.trim();
  if (!/^[+-]?[0-9]+$/.test(value)) throw new InvalidMoneyValueError(value);
  return new Big(value).div(100).toFixed(DEFAULT_MONEY_SCALE);
}

/** String decimal → centavos inteiros, arredondando meio-para-cima: `'1234.565'` → `123457`. */
export function decimalToCents(value: MoneyInput): Cents {
  const cents = Number(toDecimal(value).times(100).toFixed(0, ROUNDING_MODES['half-up']));
  if (!Number.isSafeInteger(cents)) throw new InvalidMoneyValueError(String(value));
  return cents;
}

export interface FormatMoneyOptions {
  /** Locale de exibição (separadores). A moeda é sempre BRL (U33). */
  readonly locale?: string;
  readonly scale?: number;
  readonly rounding?: MoneyRoundingMode;
  readonly style?: 'currency' | 'decimal';
  readonly signDisplay?: 'auto' | 'always' | 'never' | 'exceptZero';
}

/** Formata para exibição: `'1234.56'` → `R$ 1.234,56` (pt-BR) ou `R$1,234.56` (en). */
export function formatMoney(value: MoneyInput, options: FormatMoneyOptions = {}): string {
  const scale = options.scale ?? DEFAULT_MONEY_SCALE;
  const rounded = roundMoney(value, { scale, rounding: options.rounding ?? DEFAULT_MONEY_ROUNDING });
  const style = options.style ?? 'currency';
  const formatter = new Intl.NumberFormat(options.locale ?? 'pt-BR', {
    ...(style === 'currency' ? { style: 'currency', currency: 'BRL', currencyDisplay: 'narrowSymbol' } : { style: 'decimal' }),
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
    signDisplay: options.signDisplay ?? 'auto',
  });
  // Intl aceita string decimal (NumberFormat V3): nenhum float segura o valor. `toFixed` sempre
  // devolve `[-]dígitos[.dígitos]`, que é exatamente `${number}`.
  return formatter.format(rounded as `${number}`);
}

/** Formata centavos inteiros: `123456` → `R$ 1.234,56`. */
export function formatCents(cents: Cents | string, options: FormatMoneyOptions = {}): string {
  return formatMoney(centsToDecimal(cents), options);
}

const PT_BR_GROUPED = /^[+-]?[0-9]{1,3}(\.[0-9]{3})*(,[0-9]+)?$/;
const PT_BR_PLAIN = /^[+-]?[0-9]+(,[0-9]+)?$/;
const NOISE = /R\$|\s/g;

/** Lê valor digitado em pt-BR (`R$ 1.234,56`, `1234,5`) para string decimal, ou `null`. Estrito. */
export function parseMoneyInput(input: string): string | null {
  const cleaned = input.replace(NOISE, '');
  if (cleaned.length === 0) return null;
  if (!PT_BR_GROUPED.test(cleaned) && !PT_BR_PLAIN.test(cleaned)) return null;
  const normalized = cleaned.replaceAll('.', '').replace(',', '.');
  return normalized.startsWith('+') ? normalized.slice(1) : normalized;
}
